import { zodResolver } from '@hookform/resolvers/zod';
import { isEmailError } from '@porterin/booking-flow/src/auth/domain/helpers/authHelpers';
import {
  makeSignupFormSchema,
  SignupFormValues,
} from '@porterin/booking-flow/src/auth/domain/validations/SignupValidationSchema';
import AuthBanner from '@porterin/booking-flow/src/auth/view/components/common/AuthBanner';
import AuthCloseCmp from '@porterin/booking-flow/src/auth/view/components/common/AuthCloseCmp';
import {
  GaEvents,
  MoeEvents,
} from '@porterin/booking-flow/src/auth/view/stores/AuthAnalyticsStore';
import { AuthTexts } from '@porterin/booking-flow/src/auth/view/stores/AuthStore';
import { PrefillData } from '@porterin/booking-flow/src/auth/view/types/SignupModal';
import {
  makeBkfEventParameterPayload,
  mapPageToCategory,
} from '@porterin/booking-flow/src/utils/BookingFlowAnalyticsHelper';
import {
  publishEvent,
  publishEventParameter,
} from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import AuthTextDescCmp from '../common/AuthTextDescCmp';

import SignupFormFields from './SignupFormFields';
import { AuthModalConfig } from '../../../domain/types/AuthModalConfig';

export interface SignupModalProps {
  prefillData: PrefillData | null;
  onFormSubmit: (data: SignupFormValues) => Promise<void>;
  config: AuthModalConfig;
  closeModal: () => void;
}

const SignupModal = ({
  prefillData,
  onFormSubmit,
  closeModal,
  config,
}: SignupModalProps) => {
  const webTrackingId = '';
  const form = useForm<SignupFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(makeSignupFormSchema(config.mobileRegex)),
    defaultValues: {
      phone: prefillData?.mobile ?? '',
      name: prefillData?.name ?? '',
      email: '',
      whatsappConsent: true,
    },
  });

  useEffect(() => {
    publishEvent({
      category: mapPageToCategory(window.location.pathname),
      action: GaEvents.pageLand.signup,
    });
    publishEventParameter(
      MoeEvents.pageLand.signup,
      makeBkfEventParameterPayload(),
    );
  }, []);

  const onSubmit = async (data: SignupFormValues): Promise<void> => {
    try {
      await onFormSubmit(data);
    } catch (error: any) {
      if (isEmailError(error?.message))
        form.setError('email', { message: error?.message });
      else {
        form.setError('root', {
          message: error?.message || 'Something went wrong. Please try again.',
        });
      }
    }
  };

  return (
    <div
      id="signup-modal"
      className={`w-screen h-screen flex flex-col
        space-y-10 items-center bg-white p-6 sm:p-0
        text-typography-primary
        sm:w-[800px] sm:h-[610px] sm:rounded-[20px]
        sm:flex-row sm:space-y-0 sm:items-start relative
      `}
    >
      <AuthCloseCmp close_modal={closeModal} />
      <AuthBanner auth_state="signup" />
      <div
        className={`flex flex-col space-y-6
                    w-full sm:w-7/12
                    sm:px-14 sm:py-16
                    items-start justify-start h-full
                `}
      >
        <AuthTextDescCmp
          text={AuthTexts.signupModal.title}
          desc={AuthTexts.signupModal.description}
        />
        <SignupFormFields form={form} onSubmit={onSubmit} />
      </div>
    </div>
  );
};

export default SignupModal;
